/**
 * Fiat withdrawals: the bank details asked for each payout currency, the card details for a card
 * refund, and the checks on them. Shared by the Withdraw page (checked as the client types) and the
 * API (checked again before anything is saved).
 *
 *   GBP  Faster Payments / CHAPS   sort code + account number
 *   EUR  SEPA credit transfer      IBAN (SEPA country) + BIC
 *   USD  SWIFT wire                SWIFT/BIC + account number or IBAN, holder and bank address,
 *                                  ABA routing (US banks) and intermediary bank (both optional)
 *   card refund to a card the client deposited with: brand, last 4 digits, expiry. Never the full
 *        card number or CVV.
 */
import { WITHDRAWAL_ASSETS } from './account.js';
import { D } from './decimal.js';
export const PAYOUT_CURRENCIES = ['USD', 'EUR', 'GBP'];
/** The rail a bank payout in each currency goes by. */
export const PAYOUT_RAIL = { USD: 'SWIFT wire', EUR: 'SEPA transfer', GBP: 'Faster Payments' };
export const WITHDRAWAL_METHODS = [
    { id: 'crypto', label: 'Crypto' },
    { id: 'bank_wire', label: 'Wire transfer' },
    { id: 'card', label: 'Card' },
];
export const DEFAULT_CRYPTO_ASSET_LIMITS = () => Object.fromEntries(WITHDRAWAL_ASSETS.map((a) => [a.id, { fee: a.fee, min: a.min }]));
export const DEFAULT_WITHDRAW_LIMITS = {
    crypto: { min: '0', max: null, daily: null, assets: DEFAULT_CRYPTO_ASSET_LIMITS() },
    bank_wire: { min: '250', max: null, daily: null, fees: { USD: '0', EUR: '0', GBP: '0' } },
    card: { min: '1000', max: null, daily: null, fees: { USD: '0', EUR: '0', GBP: '0' } },
};
export const MONEY_RE = /^\d{1,9}(\.\d{1,2})?$/;
export const coinAmountRe = (dp) => new RegExp(`^\\d{1,9}(\\.\\d{1,${dp}})?$`);
/**
 * Estimate of a crypto withdrawal of `amountUsd` from the balance: the coin received after the fee, at
 * `rate` USD per coin (1 for USDT), rounded down to the coin's decimals. Null when the fee eats it all.
 */
export function cryptoReceiveEst(asset, amountUsd, rate, fee) {
    const r = D.from(rate);
    if (!r.isPos())
        return null;
    const out = D.from(amountUsd).div(r, 'down').sub(fee).round(asset.dp, 'down');
    return out.isPos() ? out : null;
}
/**
 * The other way round, for a BTC / ETH withdrawal entered in the coin: the USD taken from the balance so the
 * client receives exactly `coin`, i.e. (coin + fee) × rate rounded up to the cent. Null without a rate.
 */
export function cryptoHoldFor(coin, rate, fee) {
    const r = D.from(rate);
    if (!r.isPos())
        return null;
    return D.from(coin).add(fee).mul(r).round(2, 'up');
}
/**
 * Check and normalise limits from the admin form or API. Amounts are USD with up to 2 decimals;
 * empty max / daily mean no limit. The maximum can't be below the minimum or the fee, and the daily
 * limit can't be below the maximum (or the minimum when there is no maximum).
 */
export function parseWithdrawLimits(raw) {
    const r = (raw ?? {});
    const out = {};
    for (const { id, label } of WITHDRAWAL_METHODS) {
        const m = (r[id] ?? {});
        const amount = (k, name, optional) => {
            const v = m[k] === undefined || m[k] === null ? '' : String(m[k]).trim();
            if (!v)
                return optional ? null : new Error(`${label}: enter the ${name}`);
            if (!MONEY_RE.test(v))
                return new Error(`${label}: the ${name} must be an amount in USD with up to 2 decimals`);
            return D.from(v).toString();
        };
        const min = amount('min', 'minimum', false);
        const max = amount('max', 'maximum', true);
        const daily = amount('daily', '24-hour limit', true);
        for (const v of [min, max, daily])
            if (v instanceof Error)
                return { ok: false, error: v.message };
        const [mn, mx, dl] = [min, max, daily];
        if (mx !== null && D.from(mx).lt(mn))
            return { ok: false, error: `${label}: the maximum can't be below the minimum` };
        if (dl !== null && D.from(dl).lt(mx ?? mn))
            return { ok: false, error: `${label}: the 24-hour limit can't be below the ${mx !== null ? 'maximum' : 'minimum'}` };
        if (mx !== null && D.from(mx).isZero())
            return { ok: false, error: `${label}: a maximum of 0 blocks every withdrawal; leave it empty for no limit` };
        if (id === 'crypto') {
            // Settings saved before per-asset fees existed have no `assets`: each missing asset takes its default.
            const assets = {};
            for (const a of WITHDRAWAL_ASSETS) {
                const raw = m.assets?.[a.id];
                if (raw === undefined || raw === null) {
                    assets[a.id] = { fee: a.fee, min: a.min };
                    continue;
                }
                const fee = raw.fee === undefined || raw.fee === null ? '' : String(raw.fee).trim();
                if (!coinAmountRe(a.dp).test(fee))
                    return { ok: false, error: `${a.label}: the fee must be an amount in ${a.coin} with up to ${a.dp} decimals` };
                const amin = raw.min === undefined || raw.min === null ? '' : String(raw.min).trim();
                if (!MONEY_RE.test(amin))
                    return { ok: false, error: `${a.label}: the minimum must be an amount in USD with up to 2 decimals` };
                if (mx !== null && D.from(amin).gt(mx))
                    return { ok: false, error: `${a.label}: the minimum can't be above the crypto maximum` };
                assets[a.id] = { fee: D.from(fee).toString(), min: D.from(amin).toString() };
            }
            out.crypto = { min: mn, max: mx, daily: dl, assets };
            continue;
        }
        const fees = {};
        for (const c of PAYOUT_CURRENCIES) {
            const v = m.fees?.[c] === undefined || m.fees?.[c] === null ? '' : String(m.fees[c]).trim();
            if (!MONEY_RE.test(v))
                return { ok: false, error: `${label}: the ${c} fee must be an amount in USD with up to 2 decimals` };
            if (mx !== null && D.from(v).gte(mx))
                return { ok: false, error: `${label}: the ${c} fee must be below the maximum` };
            fees[c] = D.from(v).toString();
        }
        out[id] = { min: mn, max: mx, daily: dl, fees };
    }
    return { ok: true, limits: out };
}
// ---- checks ----
const digits = (s) => s.replace(/[\s-]/g, '');
const compact = (s) => s.replace(/\s+/g, '').toUpperCase();
export const BRAND_LABEL = { visa: 'Visa', mastercard: 'Mastercard' };
/** UK sort code: 6 digits, typed with or without dashes or spaces. */
export const validSortCode = (s) => /^\d{6}$/.test(digits(s));
export const formatSortCode = (s) => digits(s).replace(/^(\d{2})(\d{2})(\d{2})$/, '$1-$2-$3');
/** UK account number: 8 digits. */
export const validUkAccount = (s) => /^\d{8}$/.test(digits(s));
/** BIC / SWIFT: bank (4 letters), country (2 letters), location (2), optional branch (3). */
export const validBic = (s) => /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(compact(s));
/** US ABA routing number: 9 digits with the 3-7-1 weighted checksum. */
export function validAba(s) {
    const d = digits(s);
    if (!/^\d{9}$/.test(d))
        return false;
    const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
    return [...d].reduce((sum, c, i) => sum + Number(c) * w[i], 0) % 10 === 0;
}
/** IBAN length per country (SWIFT IBAN registry). */
export const IBAN_LENGTHS = {
    AD: 24, AE: 23, AL: 28, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29, BY: 28, CH: 21, CR: 22, CY: 28,
    CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, EG: 29, ES: 24, FI: 18, FO: 18, FR: 27, GB: 22, GE: 22, GI: 23, GL: 18,
    GR: 27, GT: 28, HR: 21, HU: 28, IE: 22, IL: 23, IQ: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28, LC: 32,
    LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27, MT: 31, MU: 30, NL: 18, NO: 15, PK: 24,
    PL: 28, PS: 29, PT: 25, QA: 29, RO: 24, RS: 22, SA: 24, SC: 31, SE: 24, SI: 19, SK: 24, SM: 27, ST: 25, SV: 28,
    TL: 23, TN: 24, TR: 26, UA: 29, VA: 22, VG: 24, XK: 20,
};
/** Countries whose IBANs can receive a SEPA credit transfer. */
export const SEPA_COUNTRIES = ('AD AL AT BE BG CH CY CZ DE DK EE ES FI FR GB GI GR HR HU IE IS IT LI LT LU LV MC MD ME MK MT NL NO PL PT RO SE SI SK SM VA').split(' ');
export const normalizeIban = compact;
/** "DE89370400440532013000" → "DE89 3704 0044 0532 0130 00". */
export const formatIban = (s) => compact(s).replace(/(.{4})/g, '$1 ').trim();
/** IBAN: known country, that country's length, and the ISO 13616 mod-97 checksum. */
export function validIban(s) {
    const iban = compact(s);
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(iban))
        return false;
    if (IBAN_LENGTHS[iban.slice(0, 2)] !== iban.length)
        return false;
    const moved = iban.slice(4) + iban.slice(0, 4);
    let rem = 0;
    for (const ch of moved) {
        const v = ch >= 'A' ? String(ch.charCodeAt(0) - 55) : ch;
        for (const c of v)
            rem = (rem * 10 + Number(c)) % 97;
    }
    return rem === 1;
}
/** MM/YY that has not passed (a card is valid to the end of its expiry month). */
export function validExpiry(s, now = new Date()) {
    const m = /^(\d{2})\s*\/\s*(\d{2})$/.exec(s.trim());
    if (!m)
        return false;
    const month = Number(m[1]);
    const year = 2000 + Number(m[2]);
    if (month < 1 || month > 12)
        return false;
    return year * 12 + month >= now.getUTCFullYear() * 12 + now.getUTCMonth() + 1;
}
const text = (raw, k) => String(raw[k] ?? '').replace(/\s+/g, ' ').trim();
/**
 * Check and normalise the details of a bank account (`kind` bank) or card (`kind` card) in
 * `currency`. `fingerprint` identifies the account so the same one is not saved twice.
 */
export function parsePayoutDetails(kind, currency, raw, now = new Date()) {
    const bad = (field, error) => ({ ok: false, field, error });
    const need = (k, label, max = 70) => {
        const v = text(raw, k);
        if (v.length < 2)
            return bad(k, `Enter the ${label}`);
        if (v.length > max)
            return bad(k, `The ${label} can be at most ${max} characters`);
        return v;
    };
    const holder = need('holder', kind === 'card' ? 'cardholder name' : 'account holder name');
    if (typeof holder !== 'string')
        return holder;
    if (kind === 'card') {
        const brand = text(raw, 'brand').toLowerCase();
        if (brand !== 'visa' && brand !== 'mastercard')
            return bad('brand', 'Choose Visa or Mastercard');
        const last4 = digits(text(raw, 'last4'));
        if (!/^\d{4}$/.test(last4))
            return bad('last4', 'Enter the last 4 digits of the card');
        const expiry = text(raw, 'expiry').replace(/\s/g, '');
        if (!validExpiry(expiry, now))
            return bad('expiry', 'Enter an expiry date (MM/YY) that has not passed');
        const issuer = need('issuer', 'issuing bank');
        if (typeof issuer !== 'string')
            return issuer;
        return { ok: true, details: { holder, brand, last4, expiry, issuer }, fingerprint: `card:${brand}:${last4}:${expiry}` };
    }
    const bankName = need('bankName', 'bank name');
    if (typeof bankName !== 'string')
        return bankName;
    if (currency === 'GBP') {
        const sortCode = digits(text(raw, 'sortCode'));
        if (!validSortCode(sortCode))
            return bad('sortCode', 'A sort code is 6 digits, like 12-34-56');
        const accountNumber = digits(text(raw, 'accountNumber'));
        if (!validUkAccount(accountNumber))
            return bad('accountNumber', 'A UK account number is 8 digits');
        return { ok: true, details: { holder, bankName, sortCode, accountNumber }, fingerprint: `gbp:${sortCode}:${accountNumber}` };
    }
    if (currency === 'EUR') {
        const iban = normalizeIban(text(raw, 'iban'));
        if (!validIban(iban))
            return bad('iban', 'That IBAN is not valid. Check it against your bank statement.');
        if (!SEPA_COUNTRIES.includes(iban.slice(0, 2)))
            return bad('iban', 'EUR payouts go by SEPA, so the IBAN must be from a SEPA country');
        const bic = compact(text(raw, 'bic'));
        if (!validBic(bic))
            return bad('bic', 'A BIC is 8 or 11 letters and digits, like COBADEFFXXX');
        return { ok: true, details: { holder, bankName, bankCountry: iban.slice(0, 2), iban, bic }, fingerprint: `eur:${iban}` };
    }
    const holderAddress = need('holderAddress', 'account holder address', 140);
    if (typeof holderAddress !== 'string')
        return holderAddress;
    const bankCountry = text(raw, 'bankCountry').toUpperCase();
    if (!/^[A-Z]{2}$/.test(bankCountry))
        return bad('bankCountry', 'Choose the country of the bank');
    const bankAddress = need('bankAddress', 'bank address', 140);
    if (typeof bankAddress !== 'string')
        return bankAddress;
    const swift = compact(text(raw, 'swift'));
    if (!validBic(swift))
        return bad('swift', 'A SWIFT/BIC code is 8 or 11 letters and digits, like CHASUS33');
    const account = compact(text(raw, 'account'));
    if (/^[A-Z]{2}\d{2}/.test(account) && IBAN_LENGTHS[account.slice(0, 2)]) {
        if (!validIban(account))
            return bad('account', 'That IBAN is not valid. Check it against your bank statement.');
    }
    else if (!/^[A-Z0-9]{4,34}$/.test(account)) {
        return bad('account', 'Enter the account number (4 to 34 letters and digits) or IBAN');
    }
    const abaRaw = digits(text(raw, 'aba'));
    if (abaRaw && !validAba(abaRaw))
        return bad('aba', 'An ABA routing number is 9 digits');
    const interRaw = compact(text(raw, 'intermediarySwift'));
    if (interRaw && !validBic(interRaw))
        return bad('intermediarySwift', 'A SWIFT/BIC code is 8 or 11 letters and digits');
    return {
        ok: true,
        details: { holder, holderAddress, bankName, bankCountry, bankAddress, swift, account, aba: abaRaw || null, intermediarySwift: interRaw || null },
        fingerprint: `usd:${swift}:${account}`,
    };
}
// ---- display ----
const tail = (s) => `••••${s.slice(-4)}`;
/** Masked one-liner for lists and emails, e.g. "Barclays · 20-00-00 · ••••5678". */
export function payoutSummary(kind, currency, d) {
    if (kind === 'card') {
        const c = d;
        return `${BRAND_LABEL[c.brand]} ${tail(c.last4)} · exp ${c.expiry}`;
    }
    if (currency === 'GBP') {
        const g = d;
        return `${g.bankName} · ${formatSortCode(g.sortCode)} · ${tail(g.accountNumber)}`;
    }
    if (currency === 'EUR') {
        const e = d;
        return `${e.bankName} · IBAN ${e.iban.slice(0, 4)} ${tail(e.iban)}`;
    }
    const u = d;
    return `${u.bankName} · SWIFT ${u.swift} · ${tail(u.account)}`;
}
/** Labelled rows of the full details, for staff paying the withdrawal. */
export function payoutRows(kind, currency, d) {
    if (kind === 'card') {
        const c = d;
        return [['Cardholder', c.holder], ['Card', `${BRAND_LABEL[c.brand]} ${tail(c.last4)}`], ['Expiry', c.expiry], ['Issuing bank', c.issuer]];
    }
    if (currency === 'GBP') {
        const g = d;
        return [['Account holder', g.holder], ['Sort code', formatSortCode(g.sortCode)], ['Account number', g.accountNumber], ['Bank', g.bankName]];
    }
    if (currency === 'EUR') {
        const e = d;
        return [['Account holder', e.holder], ['IBAN', formatIban(e.iban)], ['BIC', e.bic], ['Bank', e.bankName], ['Bank country', e.bankCountry]];
    }
    const u = d;
    const rows = [
        ['Account holder', u.holder],
        ['Holder address', u.holderAddress],
        ['SWIFT / BIC', u.swift],
        ['Account / IBAN', u.account],
        ['Bank', u.bankName],
        ['Bank country', u.bankCountry],
        ['Bank address', u.bankAddress],
    ];
    if (u.aba)
        rows.push(['ABA routing', u.aba]);
    if (u.intermediarySwift)
        rows.push(['Intermediary SWIFT', u.intermediarySwift]);
    return rows;
}
/**
 * Loose check that the account holder is the verified client: every word of the client's first and
 * last name appears in the holder name (titles, middle names and order are ignored). For staff, not a block.
 */
export function holderMatches(holder, firstName, lastName) {
    const words = (s) => s
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .split(/[^a-z]+/)
        .filter(Boolean);
    const have = new Set(words(holder));
    const want = words(`${firstName ?? ''} ${lastName ?? ''}`);
    return want.length > 0 && want.every((w) => have.has(w));
}
//# sourceMappingURL=payouts.js.map